import Onboard from '@web3-onboard/core';
import injectedModule from '@web3-onboard/injected-wallets';
import walletConnectModule from '@web3-onboard/walletconnect'; 
import { ethers } from 'ethers';
import axios from 'axios';
import { toast } from './app/core/Toast.js'; 

const wcV2InitOptions = {
    projectId: '17b45c3e6103ac45a22395081d3f563b', 
    requiredChains: [1], // Ethereum Mainnet
}
const walletConnect = walletConnectModule(wcV2InitOptions);
class InviteClaimController {
    constructor() {
        this.onboard = Onboard({
            wallets: [injectedModule(),walletConnect],
            chains: [{ id: '0x1', token: 'ETH', label: 'Ethereum Mainnet', rpcUrl: 'https://mainnet.infura.io/v3/2d900b08fccc41c991cee8aacee99e64' }],
            appMetadata: { name: 'SGNL', description: 'SGNL Invite Code Claim' }
        });

        this.elements = {
            connectBtn: document.getElementById('connect-btn'),
            statusArea: document.getElementById('status-area'),
            claimActionsArea: document.getElementById('claim-actions-area'),
            balanceDisplay: document.getElementById('balance-display'),
            tierDisplay: document.getElementById('tier-display'),
            claimBtn: document.getElementById('claim-btn'),
            claimMessage: document.getElementById('claim-message'),
            // --- START: ADD NEW ELEMENTS ---
            previousCodesArea: document.getElementById('previous-codes-area'),
            previousCodesList: document.getElementById('previous-codes-list'),
            // --- END: ADD NEW ELEMENTS ---
        };

        this.walletState = {
            address: null,
            signer: null,
        };
    }

    init() {
        this.elements.connectBtn?.addEventListener('click', () => this.connectAndCheckStatus());
        this.elements.claimBtn?.addEventListener('click', () => this.handleClaim());
    }

    updateStatus(message, isError = false) {
        this.elements.statusArea.textContent = message;
        this.elements.statusArea.className = `mt-2 text-sm text-center min-h-[20px] ${isError ? 'text-danger' : 'text-text-muted'}`;
    }

    async connectAndCheckStatus() {
        this.updateStatus('Connecting to wallet...');
        try {
            const wallets = await this.onboard.connectWallet();
            if (!wallets[0]) throw new Error('Wallet connection cancelled.');

            const connectedWallet = wallets[0];
            this.walletState.address = connectedWallet.accounts[0].address;
            
            const ethersProvider = new ethers.BrowserProvider(connectedWallet.provider, 'any');
            this.walletState.signer = await ethersProvider.getSigner();

            await this.checkStatus();

        } catch (error) {
            this.updateStatus(error.message || 'An error occurred.', true);
        }
    }

    async checkStatus() {
        if (!this.walletState.address) return;
        this.updateStatus('Checking your eligibility...');
        try {
            const response = await axios.get(`/api/invites/status/${this.walletState.address}`);
            this.renderStatus(response.data);
        } catch(error) {
            this.updateStatus(error.response?.data?.message || 'Could not check status.', true);
        }
    }
    
    renderStatus(data) {
        this.elements.connectBtn.classList.add('hidden');
        this.elements.claimActionsArea.classList.remove('hidden');
        this.updateStatus('');

        this.elements.balanceDisplay.textContent = `${data.balance} $UPAI`;
        this.elements.tierDisplay.textContent = data.qualified_tier ? data.qualified_tier.toUpperCase() : 'Not Eligible';

        if (data.can_claim) {
            this.elements.claimBtn.classList.remove('hidden');
            const remaining = data.codes_allowed - data.codes_claimed;
            this.elements.claimMessage.textContent = `You can claim ${remaining} more code(s).`;
        } else {
            this.elements.claimBtn.classList.add('hidden');
            this.elements.claimMessage.textContent = data.qualified_tier
                ? 'You have already claimed all your available codes.'
                : 'Your balance does not meet the minimum requirement.';
        }

        // --- START: NEW RENDERING LOGIC FOR CLAIMED CODES ---
        if (data.previously_claimed_codes && data.previously_claimed_codes.length > 0) {
            this.elements.previousCodesArea.classList.remove('hidden');
            this.elements.previousCodesList.innerHTML = ''; // Clear previous list

            data.previously_claimed_codes.forEach(code => {
                const html = this.createCodeInputHTML(code);
                this.elements.previousCodesList.insertAdjacentHTML('beforeend', html);
            });

            // Add event listeners to the new copy buttons
            this.elements.previousCodesList.querySelectorAll('.copy-code-btn').forEach(button => {
                button.addEventListener('click', (e) => this.copyCode(e.currentTarget));
            });
        } else {
            this.elements.previousCodesArea.classList.add('hidden');
        }
        // --- END: NEW RENDERING LOGIC ---
    }

    async handleClaim() {
        if (!this.walletState.address || !this.walletState.signer) {
            return this.updateStatus('Wallet not connected.', true);
        }

        this.updateStatus('Please sign the message in your wallet...');
        this.elements.claimBtn.disabled = true;

        try {
            const message = `Claim invite code for SGNL with address: ${this.walletState.address}`;
            const signature = await this.walletState.signer.signMessage(message);

            this.updateStatus('Processing your claim...');
            const response = await axios.post('/api/invites/claim', {
                address: this.walletState.address,
                signature: signature
            });
            
            toast.show(response.data.message, 'success');
            await this.checkStatus(); // Re-fetch status to update the UI with the new code

        } catch (error) {
            this.updateStatus(error.response?.data?.message || 'Claim failed.', true);
        } finally {
            this.elements.claimBtn.disabled = false;
        }
    }
    
    // --- START: NEW HELPER METHODS ---
    createCodeInputHTML(code) {
        return `
            <div class="relative">
                <input type="text" readonly value="${code}" class="w-full text-center bg-surface border-0 text-text-base font-semibold text-sm rounded-md tracking-wider pr-10">
                <button class="copy-code-btn absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-primary">
                    <svg class="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                </button>
            </div>
        `;
    }
    
    copyCode(button) {
        const input = button.previousElementSibling;
        input.select();
        document.execCommand('copy');
        
        button.innerHTML = `<svg class="w-5 h-5 text-success pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>`;
        setTimeout(() => {
            button.innerHTML = `<svg class="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>`;
        }, 2000);
    }
    // --- END: NEW HELPER METHODS ---
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('claim-container')) {
        const controller = new InviteClaimController();
        controller.init();
    }
});